"use client";

import type React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Button,
  VStack,
  useToast,
  Box,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  IconButton,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { ChevronDownIcon, AddIcon } from "@chakra-ui/icons";
import { FaPlayCircle, FaStopCircle } from "react-icons/fa";
import {
  agentService,
  type VoiceDetails,
} from "../../../services/agentService.ts";
import { useAppTheme } from "../../../ThemeContext.tsx";
import GenderImage from "../voices/GenderImages.tsx";

interface FallbackVoiceSelectProps {
  agentId: string;
  initialFallbackVoiceIds: string[] | null;
}

export const FallbackVoiceSelect: React.FC<FallbackVoiceSelectProps> = ({
  agentId,
  initialFallbackVoiceIds,
}) => {
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedVoices, setSelectedVoices] = useState<VoiceDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  // Get unique providers from voices array
  const providers = useCallback(() => {
    const uniqueProviders = Array.from(
      new Set(
        voices
          .map((voice) => voice.provider)
          .filter((provider) => provider !== "playht")
      )
    );
    return uniqueProviders.sort();
  }, [voices]);

  // Load available voices
  useEffect(() => {
    const loadVoices = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);

        // If we have initial fallback voice IDs, find their details
        if (initialFallbackVoiceIds) {
          const initialVoices = voicesResponse.filter((voice) =>
            initialFallbackVoiceIds.includes(voice.voice_id)
          );
          setSelectedVoices(initialVoices);
        }
      } catch (error) {
        console.error("Error loading voices:", error);
        toast({
          title: "Error loading voices",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadVoices();
  }, [initialFallbackVoiceIds, toast]);

  // Setup audio element
  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio();

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Stop audio when menu closes
  useEffect(() => {
    if (!isMenuOpen && audioRef.current && currentlyPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentlyPlaying(null);
    }
  }, [isMenuOpen, currentlyPlaying]);

  const playAudio = useCallback(
    (voiceId: string, audioUrl: string | undefined, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent menu item click

      if (!audioUrl) {
        toast({
          title: "Preview not available",
          description: "No preview audio available for this voice",
          status: "info",
          duration: 2000,
        });
        return;
      }

      // Stop current audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // If clicking on the same voice that's already playing, just stop it
      if (currentlyPlaying === voiceId) {
        setCurrentlyPlaying(null);
        return;
      }

      // Play new audio
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play().catch((error) => {
          console.error("Error playing audio:", error);
          toast({
            title: "Error playing preview",
            description: "Could not play the audio preview",
            status: "error",
            duration: 2000,
          });
        });

        setCurrentlyPlaying(voiceId);

        // Reset when audio ends
        audioRef.current.onended = () => {
          setCurrentlyPlaying(null);
        };
      }
    },
    [currentlyPlaying, toast]
  );

  // const updateFallbackVoices = async (voiceIds: string[] | null) => {
  //   setIsUpdating(true);
  //   try {
  //     await agentService.updateAgentSettings(agentId, {
  //       fallback_voice_ids: voiceIds,
  //     });

  //     setSelectedVoiceIds(voiceIds || []);
  //     toast({
  //       title: "Fallback voices updated",
  //       status: "success",
  //       duration: 2000,
  //     });
  //   } catch (error: any) {
  //     console.error("Error updating fallback voices:", error);
  //     toast({
  //       title: "Error updating fallback voices",
  //       description:
  //         error.response?.data?.message || "An unexpected error occurred",
  //       status: "error",
  //       duration: 3000,
  //     });
  //   } finally {
  //     setIsUpdating(false);
  //   }
  // };

  const handleAddVoice = useCallback(
    async (voice: VoiceDetails) => {
      if (isUpdating) return;

      const newSelectedVoices = [...selectedVoices, voice];
      setIsUpdating(true);

      try {
        await agentService.update(agentId, {
          fallback_voice_ids: newSelectedVoices.map((v) => v.voice_id),
        });

        setSelectedVoices(newSelectedVoices);
        toast({
          title: "Fallback voice added",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating fallback voices:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "An unknown error occurred";
        toast({
          title: "Error updating fallback voices",
          description: errorMessage,
          status: "error",
          duration: 5000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, selectedVoices, isUpdating, toast]
  );

  const handleRemoveVoice = useCallback(
    async (voiceId: string) => {
      if (isUpdating) return;

      const newSelectedVoices = selectedVoices.filter(
        (v) => v.voice_id !== voiceId
      );
      setIsUpdating(true);

      try {
        await agentService.update(agentId, {
          fallback_voice_ids:
            newSelectedVoices.length > 0
              ? newSelectedVoices.map((v) => v.voice_id)
              : null,
        });

        setSelectedVoices(newSelectedVoices);
        toast({
          title: "Fallback voice removed",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating fallback voices:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "An unknown error occurred";
        toast({
          title: "Error updating fallback voices",
          description: errorMessage,
          status: "error",
          duration: 5000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, selectedVoices, isUpdating, toast]
  );

  // Filter out already selected voices
  const availableVoices = voices.filter(
    (voice) =>
      !selectedVoices.some((selected) => selected.voice_id === voice.voice_id)
  );

  // Helper function to format provider name
  const formatProviderName = (provider: string) => {
    return provider.charAt(0).toUpperCase() + provider.slice(1);
  };

  // Render voice item
  const renderVoiceItem = (voice: VoiceDetails) => (
    <MenuItem
      key={voice.voice_id}
      onClick={() => handleAddVoice(voice)}
      bg="transparent"
      _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
      color={isDark ? "white" : "gray.800"}
      py={3}
    >
      <Flex direction="column" width="100%">
        <Flex align="center" width="100%">
          <IconButton
            aria-label="Play voice sample"
            icon={
              <Box
                as="span"
                fontSize="sm"
                color={isDark ? "gray.400" : "gray.600"}
                borderRadius="full"
              >
                {currentlyPlaying === voice.voice_id ? (
                  <FaStopCircle />
                ) : (
                  <FaPlayCircle />
                )}
              </Box>
            }
            size="xs"
            variant="ghost"
            mr={2}
            onClick={(e) =>
              playAudio(voice.voice_id, voice.preview_audio_url, e)
            }
          />
          <GenderImage voice={voice} isDark={isDark} />
          <Text fontWeight="medium">{voice?.voice_name}</Text>
        </Flex>
        <Flex
          ml="36px"
          mt={1}
          fontSize="xs"
          color={isDark ? "gray.400" : "gray.600"}
        >
          <Text mr={2}>{voice.gender}</Text>
          {voice.accent && (
            <>
              <Text mr={2}>•</Text>
              <Text mr={2}>{voice.accent}</Text>
            </>
          )}
          {voice.age && (
            <>
              <Text mr={2}>•</Text>
              <Text>{voice.age}</Text>
            </>
          )}
        </Flex>
      </Flex>
    </MenuItem>
  );

  return (
    <FormControl>
      <FormLabel
        fontSize="sm"
        fontWeight="medium"
        color={isDark ? "gray.300" : "gray.700"}
        mb={1}
      >
        Fallback Voice ID
      </FormLabel>
      <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={3}>
        If the current voice provider fails, assign a fallback voice to continue
        the call.
      </Text>

      <VStack spacing={4} align="stretch">
        {selectedVoices.length > 0 && (
          <Box>
            <VStack spacing={2} align="stretch">
              {selectedVoices.map((voice) => (
                <Tag
                  key={voice.voice_id}
                  size="md"
                  borderRadius="md"
                  variant="subtle"
                  colorScheme="gray"
                  color={isDark ? "gray.300" : "gray.700"}
                  bg={isDark ? "gray.700" : "gray.50"}
                  py={2}
                >
                  <HStack spacing={2} flex={1} px={2}>
                    <GenderImage voice={voice} isDark={isDark} />
                    <TagLabel>
                      <HStack spacing={1}>
                        <Text>{voice?.voice_name}</Text>
                        <Text
                          color={isDark ? "gray.400" : "gray.500"}
                          fontSize="xs"
                        >
                          ({formatProviderName(voice.provider)})
                        </Text>
                      </HStack>
                    </TagLabel>
                  </HStack>
                  <TagCloseButton
                    onClick={() => handleRemoveVoice(voice.voice_id)}
                  />
                </Tag>
              ))}
            </VStack>
          </Box>
        )}

        <Box position="relative">
          <Menu
            isOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
          >
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              leftIcon={<AddIcon />}
              size="xs"
              variant="outline"
              isLoading={isLoading}
              isDisabled={isUpdating || availableVoices.length === 0}
              color={isDark ? "gray.300" : "gray.700"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
              w="100%"
            >
              Add Fallback Voice
            </MenuButton>
            <Portal>
              <MenuList
                bg={isDark ? "gray.800" : "white"}
                color={isDark ? "gray.300" : "gray.700"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                maxH="300px"
                overflowY="auto"
                zIndex={2000}
                p={0}
                sx={{
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: isDark ? "gray.700" : "gray.100",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: isDark ? "gray.600" : "gray.300",
                    borderRadius: "6px",
                  },
                  // Firefox
                  scrollbarWidth: "thin",
                  scrollbarColor: isDark
                    ? "#4A5568 #2D3748"
                    : "#CBD5E0 #EDF2F7",
                }}
              >
                {providers().length > 0 ? (
                  <Tabs variant="enclosed" isFitted size="sm">
                    <TabList>
                      {providers().map((provider) => (
                        <Tab
                          key={provider}
                          _selected={{
                            color: isDark ? "white" : "gray.800",
                            bg: isDark ? "gray.700" : "gray.100",
                            fontWeight: "semibold",
                          }}
                          color={isDark ? "gray.300" : "gray.600"}
                          bg={isDark ? "gray.800" : "white"}
                          borderBottomWidth="1px"
                          borderBottomColor={isDark ? "gray.600" : "gray.200"}
                          _hover={{ bg: isDark ? "gray.700" : "gray.50" }}
                        >
                          {formatProviderName(provider)}
                        </Tab>
                      ))}
                    </TabList>
                    <TabPanels>
                      {providers().map((provider) => (
                        <TabPanel key={provider} p={0}>
                          {availableVoices
                            .filter((voice) => voice.provider === provider)
                            .map(renderVoiceItem)}
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                ) : (
                  <Box p={4} textAlign="center">
                    <Text>No available fallback voices found.</Text>
                  </Box>
                )}
              </MenuList>
            </Portal>
          </Menu>
        </Box>
      </VStack>
    </FormControl>
  );
};
